// Anthropic configuration
export const ANTHROPIC_CONFIG = {
    model: 'claude-3-5-sonnet-20241022',
    max_tokens: 8192,
    stream: true,
    system: "Speak in complete sentences. Avoid using lists if possible. Use bold to **highlight specific terminology.** More guidelines: Precise Terminology - If you know the exact term for something, use it. Avoid watered down or generic language. Scientific jargon is acceptable. Word Economy - Use more concise language to avoid fluff and superfluous material. Maintain a high insight-to-word ratio. Keep your responses full length."
};

// Message window configuration
const MAX_MESSAGES = 10; // Keep last 10 messages for context

// Function to get last N messages
function getLastMessages(messages) {
    return messages.slice(-MAX_MESSAGES);
}

export const streamAnthropicResponse = async function*(promptToUse, messages, config) {
    const { apiKey, useProxy, user } = config;

    // Force proxy mode if VITE_ANTHROPIC_API_KEY is 'PROXY'
    const shouldUseProxy = useProxy ||
        import.meta.env.VITE_ANTHROPIC_API_KEY === 'PROXY';

    const endpoint = shouldUseProxy ?
        `${import.meta.env.VITE_WORKER_URL}/api/chat` :
        'https://api.anthropic.com/v1/messages';

    // Get last N messages for context
    const contextMessages = getLastMessages(messages);
    console.log(`Using ${contextMessages.length} messages for context out of ${messages.length} total`);

    const requestBody = {
        model: ANTHROPIC_CONFIG.model,
        max_tokens: ANTHROPIC_CONFIG.max_tokens,
        stream: true,
        messages: [...contextMessages, { role: 'user', content: promptToUse }],
        system: ANTHROPIC_CONFIG.system,
        ...(shouldUseProxy && {
            apiUrl: 'https://api.anthropic.com/v1/messages',
            headers: {
                'anthropic-version': '2023-06-01',
                'Content-Type': 'application/json'
            },
            userId: user && user.uid,
            email: user && user.email
        })
    };

    const headers = shouldUseProxy ? {
            'Content-Type': 'application/json',
            'X-Service-Key': 'ANTHROPIC_API_KEY', // Service key in separate header
            'Authorization': `Bearer ${await user.getIdToken()}` // Firebase token for auth
        } :
        // Direct mode - include auth header
        {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01'
        };

    // Debug request
    console.log('Sending request:', {
        endpoint,
        headers: {...headers, Authorization: '[REDACTED]' },
        body: requestBody
    });

    const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        const error = await response.text();
        throw new Error(`API request failed: ${error}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    const data = line.slice(6);
                    if (data === '[DONE]') return;

                    try {
                        const parsed = JSON.parse(data);
                        console.log('Parsed SSE event:', parsed.type, parsed);

                        // Yield token counts as special events
                        if (parsed.type === 'message_start' && parsed.message && parsed.message.usage && parsed.message.usage.input_tokens) {
                            console.log('Found input tokens:', parsed.message.usage.input_tokens);
                            yield { type: 'tokens', counts: { input: parsed.message.usage.input_tokens } };
                        }
                        if (parsed.type === 'message_delta' && parsed.usage && parsed.usage.output_tokens) {
                            console.log('Found output tokens:', parsed.usage.output_tokens);
                            yield { type: 'tokens', counts: { output: parsed.usage.output_tokens } };
                        }

                        // Yield content as before
                        if (parsed.delta && parsed.delta.text) {
                            yield parsed.delta.text;
                        }
                    } catch (e) {
                        console.warn('Failed to parse chunk:', e);
                    }
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}